import {Component} from "../../../../../../../sedestral/framework/interface/component/Component";
import {SedestralMachine} from "../../../../../../../sedestral/framework/machine/SedestralMachine";
import * as s from "./agent-avatar.scss";
import {AgentAvatarBody} from "./AgentAvatarBody";
import {AgentAvatarEyes} from "./AgentAvatarEyes";
import {AgentAvatarLooks} from "./AgentAvatarLooks";
import {AgentAvatarState} from "./types/AgentAvatarState";
import {AgentAvatarReaction} from "./types/AgentAvatarReaction";
import {IAgentAvatarOptions} from "./types/IAgentAvatarOptions";
import {IAgentAvatarIdentity} from "./types/IAgentAvatarIdentity";
import {IAgentAvatarEyeRender} from "./types/IAgentAvatarEyeRender";

export class AgentAvatarComponent extends Component {

    private static PARTICLE_COLORS = ["#ffd166", "#06d6a0", "#8ecae6", "var(--c)"];
    private static EASE_MILLIS = 450;
    private static GLYPH_FADE_MILLIS = 220;
    private static requestFrame = SedestralMachine.requestFrame();

    public options: IAgentAvatarOptions;
    public identity: IAgentAvatarIdentity;
    public state: AgentAvatarState;
    public reaction: AgentAvatarReaction;

    public image: Component;
    public body: Component;
    public reactionBox: Component;
    public gaze: Component;
    public badge: Component;
    public glyphSymbol: Component;
    public eyes: AgentAvatarEyes;

    private eyePaths: SVGPathElement[];
    private glints: SVGCircleElement[];
    private reactionToken = 0;
    private settleToken = 0;
    private easings: Map<HTMLElement, Animation> = new Map();
    private stopped = false;
    private outOfView = false;
    private pointerPending = false;
    private gazeHeld = false;
    private pendingReaction: AgentAvatarReaction;

    constructor(options: IAgentAvatarOptions) {
        super();
        this.options = options;
        this.identity = AgentAvatarBody.identity(options.persona);
        this.state = options.state ?? AgentAvatarState.IDLE;
        this.pendingReaction = options.reaction;

        //language=HTML
        this.template = `
            <div class="${s.componentAgentAvatar}${options.mirrored ? ` ${s.mirrored}` : ``}" data-state="${this.state}"
                 style="--c:${this.identity.color};--delay:${(-Math.random() * 9).toFixed(2)}s;--blink:${(4.5 + Math.random() * 3).toFixed(2)}s;--blink-delay:${(-Math.random() * 7).toFixed(2)}s;--look:${(8 + Math.random() * 4).toFixed(2)}s;--eye-y:${AgentAvatarBody.toPercent(AgentAvatarBody.shape(this.identity.shape).eyeY)};">
                <div class="${s.shadow}"></div>
                <div class="${s.back}">
                    <div class="${s.orbit}">${[0, 1, 2].map(() => `<div class="${s.dot}"></div>`).join("")}</div>
                    <div class="${s.rings}">${[0, 1].map(() => `<div class="${s.ring}"></div>`).join("")}</div>
                    <div class="${s.waves}">${[0, 1].map(() => `<div class="${s.arc}"></div>`).join("")}</div>
                </div>
                <div class="${s.body}">
                    <div class="${s.reaction}">
                        <img class="${s.image}" src="${AgentAvatarBody.image(this.identity)}" alt="" draggable="false" decoding="async"/>
                        <div class="${s.gaze}">
                            <div class="${s.scan}">
                                <div class="${s.lids}">
                                    <svg class="${s.eyes}" viewBox="${AgentAvatarBody.VIEW_BOX}">
                                        <path class="${s.eye}"></path>
                                        <path class="${s.eye}"></path>
                                        <circle class="${s.glint}" r="0"></circle>
                                        <circle class="${s.glint}" r="0"></circle>
                                    </svg>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="${s.front}">
                    <div class="${s.orbit}">${[0, 1, 2].map(() => `<div class="${s.dot}"></div>`).join("")}</div>
                    <div class="${s.sonar}">${[0, 1, 2].map(() => `<div class="${s.arc}"></div>`).join("")}</div>
                    <div class="${s.think}">${[0, 1, 2].map(() => `<div class="${s.dot}"></div>`).join("")}</div>
                    <div class="${s.zzz}">${[0, 1, 2].map(() => `<span class="${s.letter}">z</span>`).join("")}</div>
                    <div class="${s.write}">
                        <div class="${s.line}"></div>
                        <div class="${s.pen}"></div>
                    </div>
                    <div class="${s.glyph}"><span class="${s.symbol}"></span></div>
                    <div class="${s.badge}"><div class="${s.badgeDot}"></div></div>
                    <div class="${s.particles}">${Array.from({length: 12}, (_, index) => {
                        let angle = index / 12 * Math.PI * 2 + Math.random() * .3;
                        let distance = 900 + Math.random() * 700;
                        return `<span class="${s.particle}" style="--dx:${(Math.cos(angle) * distance).toFixed(0)}%;--dy:${(Math.sin(angle) * distance - 400).toFixed(0)}%;background:${AgentAvatarComponent.PARTICLE_COLORS[index % 4]};"></span>`;
                    }).join("")}</div>
                </div>
            </div>
        `;

        this.onCommit(() => {
            this.image = this.el(s.image);
            this.body = this.el(s.body);
            this.reactionBox = this.el(s.reaction);
            this.gaze = this.el(s.gaze);
            this.badge = this.el(s.badgeDot);
            this.glyphSymbol = this.el(s.symbol);
            this.eyePaths = this.els(s.eye).map(eye => eye.element as SVGPathElement);
            this.glints = this.els(s.glint).map(glint => glint.element as SVGCircleElement);

            this.eyes = new AgentAvatarEyes((eyes: IAgentAvatarEyeRender[]) => this.renderEyes(eyes));
            this.applyIdentity();
            this.setAttribute("data-state", this.state);
            this.applyStateLook();

            // son apparition jouée, il ne la rejoue plus en revenant à l'écran ou en reprenant après une pause
            this.timeOut(() => this.addClass(s.appeared), AgentAvatarComponent.EASE_MILLIS);

            this.onVisibilityChange((visible: boolean) => {
                this.outOfView = !visible;
                this.refreshPlayback();
            });

            if (options.followPointer) {
                this.putListener(window, "pointermove", (event: PointerEvent) => this.followPointer(event));
            }

            if (this.pendingReaction) {
                this.react(this.pendingReaction);
            }
        });

        this.onRemove(() => this.eyes?.stop());
    }

    public setPersona(persona: number): void {
        this.identity = AgentAvatarBody.identity(persona);
        if (this.eyes != undefined) {
            this.applyIdentity();
        }
    }

    public setState(state: AgentAvatarState): void {
        if (state == this.state) {
            return;
        }
        const previous = this.state;
        this.state = state;
        if (this.eyes == undefined || this.isNull()) {
            return;
        }
        if (!this.gazeHeld) {
            this.removeClass(s.follow);
        }
        this.settleDecorations();
        this.easeAround([this.body, this.el(s.scan), this.el(s.lids)], () => this.setAttribute("data-state", state));
        this.applyStateLook(previous);
        if (state == AgentAvatarState.DONE) {
            this.burst();
        }
        this.eyes.morph(this.expression());
    }

    public getState(): AgentAvatarState {
        return this.state;
    }

    public react(reaction: AgentAvatarReaction): void {
        if (this.eyes == undefined) {
            this.pendingReaction = reaction;
            return;
        }
        const look = AgentAvatarLooks.REACTIONS[reaction];
        // figé ou hors de l'écran, il ne la jouerait qu'à moitié : reprise en route, coupée par sa fin
        if (!look || this.isNull() || this.stopped || this.outOfView) {
            return;
        }
        this.pendingReaction = undefined;
        if (this.reaction) {
            this.fadeGlyph();
        }
        this.reaction = reaction;
        this.easeAround([this.reactionBox], () => this.removeAttribute("data-reaction"));
        this.glyphSymbol.setText(look.glyph ?? "");
        this.glyphSymbol.setStyle(`--glyph:${look.glyphColor ?? "var(--c)"};`);
        this.eyes.morph(look.eyes);

        const token = ++this.reactionToken;
        this.timeOut(() => token == this.reactionToken && this.setAttribute("data-reaction", reaction), 30);
        this.timeOut(() => {
            if (token != this.reactionToken) {
                return;
            }
            this.reaction = undefined;
            this.fadeGlyph();
            this.easeAround([this.reactionBox], () => this.removeAttribute("data-reaction"));
            this.eyes.morph(this.expression());
        }, Math.max(1600, look.duration * 1000 + 330));
    }

    /**
     * Il tourne le regard vers un point de l'écran (un bouton, un autre agent) et le garde jusqu'à lookAway, même
     * quand la souris bouge ou que son état change.
     */
    public lookAt(clientX: number, clientY: number): void {
        if (this.eyes == undefined || this.isNull()) {
            return;
        }
        this.gazeHeld = true;
        let offsets = this.getOffsets();
        let size = this.getWidth();
        let deltaX = clientX - offsets.left - size / 2;
        let deltaY = clientY - offsets.top - size / 2;
        let distance = Math.hypot(deltaX, deltaY) || 1;
        let reach = Math.min(1, distance / 250);
        this.addClass(s.follow);
        this.gaze.setStyle(`transform:translate(${(deltaX / distance * 6 * reach * this.direction()).toFixed(2)}%,${(deltaY / distance * 3.8 * reach).toFixed(2)}%);`);
    }

    public lookAway(): void {
        if (!this.gazeHeld) {
            return;
        }
        this.gazeHeld = false;
        if (this.eyes == undefined || this.isNull()) {
            return;
        }
        this.removeClass(s.follow);
        this.applyStateLook();
    }

    public play(): void {
        this.stopped = false;
        this.refreshPlayback();
    }

    public stop(): void {
        this.stopped = true;
        this.refreshPlayback();
    }

    public isPlaying(): boolean {
        return !this.stopped;
    }

    private refreshPlayback(): void {
        if (this.eyes == undefined) {
            return;
        }
        if (this.stopped || this.outOfView) {
            this.addClass(s.paused);
            this.eyes.stop();
        } else if (this.hasClass(s.paused)) {
            this.removeClass(s.paused);
            this.eyes.morph(this.expression());
        }
    }

    private expression(): string {
        return this.reaction ? AgentAvatarLooks.REACTIONS[this.reaction].eyes : AgentAvatarLooks.STATES[this.state].eyes;
    }

    private applyIdentity(): void {
        let shape = AgentAvatarBody.shape(this.identity.shape);
        this.image.setAttribute("src", AgentAvatarBody.image(this.identity));
        this.setStyle(`--c:${this.identity.color};--eye-y:${AgentAvatarBody.toPercent(shape.eyeY)};`);
        this.eyes.place([[shape.eyeX - 20, shape.eyeY], [shape.eyeX + 20, shape.eyeY]], this.expression());
    }

    private applyStateLook(previous?: AgentAvatarState): void {
        const look = AgentAvatarLooks.STATES[this.state];
        if (!this.gazeHeld) {
            this.gaze.setStyle(`transform:${look.gaze ?? "none"};`);
        }
        if (!look.badgeText) {
            return;
        }
        const shown = previous ? AgentAvatarLooks.STATES[previous]?.badgeText : undefined;
        this.badge.setText(look.badgeText);
        this.badge.setStyle(`--badge:${look.badgeColor};`);
        const badge = this.badge.getHTMLElement();
        if (shown && shown != look.badgeText && badge?.animate && !this.hasClass(s.paused)) {
            badge.animate([{transform: "scale(.55)", opacity: .35}, {transform: "none", opacity: 1}],
                {duration: 280, easing: "cubic-bezier(.3, 1.4, .5, 1)"});
        }
    }

    /**
     * Les signes d'un état caché ne tournent pas pour rien : ils ne s'animent que visibles, et le
     * temps de s'effacer après un changement d'état.
     */
    private settleDecorations(): void {
        const token = ++this.settleToken;
        this.addClass(s.settling);
        this.timeOut(() => token == this.settleToken && this.removeClass(s.settling), AgentAvatarComponent.EASE_MILLIS + 100);
    }

    /**
     * Un mouvement ne remplace pas le précédent d'un coup (nouvel état, réaction qui en coupe une
     * autre) : chaque pièce part de la pose exacte où elle était et rejoint en douceur le nouveau
     * mouvement, qui démarre dessous.
     */
    private easeAround(components: Component[], change: () => void): void {
        const elements = components.map(component => component?.getHTMLElement()).filter(element => !!element?.animate);
        if (this.hasClass(s.paused) || elements.length == 0 || typeof DOMMatrix == "undefined") {
            elements.forEach(element => this.easings.get(element)?.cancel());
            change();
            return;
        }
        const before = elements.map(element => this.matrixOf(element));
        elements.forEach(element => this.easings.get(element)?.cancel());
        change();
        elements.forEach((element, index) => {
            try {
                const from = this.matrixOf(element).inverse().multiply(before[index]);
                if (from.isIdentity) {
                    return;
                }
                this.easings.set(element, element.animate([{transform: from.toString()}, {transform: "none"}],
                    {duration: AgentAvatarComponent.EASE_MILLIS, easing: "cubic-bezier(.3, .7, .3, 1)", composite: "add"}));
            } catch (error) {
                this.easings.delete(element);
            }
        });
    }

    private matrixOf(element: HTMLElement): DOMMatrix {
        const transform = getComputedStyle(element).transform;
        return transform && transform != "none" ? new DOMMatrix(transform) : new DOMMatrix();
    }

    /**
     * Le signe d'une réaction coupée par une autre (cœur, point d'exclamation) ne disparaît pas d'un
     * coup : un double s'efface là où il était pendant que la nouvelle réaction démarre.
     */
    private fadeGlyph(): void {
        const symbol = this.glyphSymbol?.getHTMLElement();
        const glyph = symbol?.parentElement;
        if (!symbol || !glyph || !symbol.animate || this.hasClass(s.paused)) {
            return;
        }
        const style = getComputedStyle(symbol);
        const opacity = Number(style.opacity);
        if (!(opacity > .02)) {
            return;
        }
        const ghost = glyph.cloneNode(true) as HTMLElement;
        const ghostSymbol = ghost.firstElementChild as HTMLElement;
        ghost.style.display = "block";
        ghostSymbol.style.animation = "none";
        ghostSymbol.style.opacity = String(opacity);
        ghostSymbol.style.transform = style.transform;
        glyph.after(ghost);
        ghostSymbol.animate([{opacity: opacity}, {opacity: 0}],
            {duration: AgentAvatarComponent.GLYPH_FADE_MILLIS, easing: "ease-out", fill: "forwards"});
        this.timeOut(() => ghost.remove(), AgentAvatarComponent.GLYPH_FADE_MILLIS + 30);
    }

    private burst(): void {
        this.removeClass(s.burst);
        this.timeOut(() => this.addClass(s.burst), 30);
        this.timeOut(() => this.removeClass(s.burst), 1000);
    }

    private renderEyes(eyes: IAgentAvatarEyeRender[]): void {
        eyes.forEach((eye, index) => {
            this.eyePaths[index].setAttribute("d", eye.path);
            this.glints[index].setAttribute("cx", eye.glintX.toFixed(1));
            this.glints[index].setAttribute("cy", eye.glintY.toFixed(1));
            this.glints[index].setAttribute("r", eye.glintRadius.toFixed(1));
        });
    }

    /**
     * Un agent retourné (posé à gauche d'un écran, tourné vers le centre) regarde à l'inverse de son dessin.
     */
    private direction(): number {
        return this.options.mirrored ? -1 : 1;
    }

    private followPointer(event: PointerEvent): void {
        if (this.pointerPending || this.gazeHeld || this.state != AgentAvatarState.IDLE || this.outOfView || this.stopped) {
            return;
        }
        this.pointerPending = true;
        AgentAvatarComponent.requestFrame(() => {
            this.pointerPending = false;
            if (this.isNull()) {
                return;
            }
            let offsets = this.getOffsets();
            let size = this.getWidth();
            let x = Math.max(-6, Math.min(6, (event.clientX - offsets.left - size / 2) / (window.innerWidth / 2) * 9));
            let y = Math.max(-3.8, Math.min(3.8, (event.clientY - offsets.top - size / 2) / (window.innerHeight / 2) * 6));
            this.addClass(s.follow);
            this.gaze.setStyle(`transform:translate(${(x * this.direction()).toFixed(2)}%,${y.toFixed(2)}%);`);
        });
    }
}
